import {createAsyncThunk} from '@reduxjs/toolkit'

import {getCancelToken} from '@shared/helpers/api'
import {formatDateFns} from '@shared/helpers/date'
import {filterValueKeys} from '@shared/helpers/object'
import {qs} from '@shared/helpers/queryString'
import {SchemasOrderSaleHistory} from '@shared/schemas/order'

import {ROUTER_ORDER_SALE_HISTORY} from '@web/core/routes'
import getApi, {createApiAsyncThunk, createWrapper} from '@web/helpers/api'
import {push, replace} from '@web/slices/router'

import {
    SaleHistoryAssignmentsDetailResponse,
    SaleHistoryAssignmentsResponse,
    SaleHistoryDetailResponse,
    SaleHistoryFilterResponse,
    SaleHistoryNextResponse,
    SaleHistoryResponse,
} from './apis/interfaces'

import type {ThunkApi} from './interfaces'

const ApiBaseUrl = `/orderApi/orderSheet/sale/history`

const api = getApi(ApiBaseUrl)

const actionType = 'saleHistory'
const SaleHistoryUrl = ROUTER_ORDER_SALE_HISTORY.MAIN.getPath()
const SaleHistorySearchUrl = ROUTER_ORDER_SALE_HISTORY.SEARCH.getPath()

type SearchParams = {
    search?: 'filter' | 'advanced'
    keyword?: string
    category?: string
    startDate?: string
    endDate?: string
}

const getUrl = ({
    url,
    page,
    search,
    keyword,
    category,
    startDate,
    endDate,
}: SearchParams & {
    url: string
    page?: number
}) => {
    return qs.stringifyUrlBySort<{
        page?: Maybe<string>
        search?: Maybe<string>
        keyword?: Maybe<string>
        category?: Maybe<string>
        startDate?: Maybe<string>
        endDate?: Maybe<string>
    }>({
        url,
        query: filterValueKeys((v) => !!v, {
            page: page && page > 0 ? page : null,
            search: search ? `${search}`.trim() : null,
            keyword: keyword ? `${keyword}`.trim() : null,
            category: category || null,
            startDate: startDate ? formatDateFns(new Date(startDate), 'yyyy-MM-dd') : null,
            endDate: endDate ? formatDateFns(new Date(endDate), 'yyyy-MM-dd') : null,
        }),
        sorts: ['search', 'startDate', 'endDate', 'page', 'category', 'keyword'],
    })
}

export const getSaleHistoryByApi = createApiAsyncThunk<
    SaleHistoryResponse,
    SearchParams & {
        page?: string
    },
    ThunkApi<{
        code: string
        fallbackMessage: string
    }>
>(`${actionType}/getSaleHistoryByApi`, async ({page, keyword, category, startDate, endDate}, {signal}) => {
    const source = getCancelToken()
    signal.addEventListener('abort', () => {
        source.cancel()
    })
    return createWrapper<SchemasOrderSaleHistory['/']['response']>(
        api.get('/', {
            params: {
                page,
                keyword,
                category,
                startDate,
                endDate,
            },
            cancelToken: source.token,
        }),
    )
})

export const getSaleHistorySearchByApi = createApiAsyncThunk<
    SaleHistoryNextResponse,
    SearchParams & {
        nextCursor?: string
    },
    ThunkApi<{
        code: string
        fallbackMessage: string
    }>
>(
    `${actionType}/getSaleHistorySearchNextByApi`,
    async ({keyword, category, search, startDate, endDate, nextCursor}, {signal}) => {
        const source = getCancelToken()
        signal.addEventListener('abort', () => {
            source.cancel()
        })
        return createWrapper<SchemasOrderSaleHistory['/search/:type']['response']>(
            api.get(`/search/${search === 'filter' ? 'filter' : 'advanced'}`, {
                params: {
                    keyword,
                    category,
                    startDate,
                    endDate,
                    nextCursor,
                },
                cancelToken: source.token,
            }),
        )
    },
)

export const getSaleHistoryFilterByApi = createApiAsyncThunk<
    SaleHistoryFilterResponse,
    void,
    ThunkApi<{
        code: string
        fallbackMessage: string
    }>
>(`${actionType}/getSaleHistoryFilterByApi`, async (_, {signal}) => {
    const source = getCancelToken()
    signal.addEventListener('abort', () => {
        source.cancel()
    })
    return createWrapper<SchemasOrderSaleHistory['/filter']['response']>(
        api.get('/filter', {
            cancelToken: source.token,
        }),
    )
})

export const deleteSaleHistoryDetailByApi = createApiAsyncThunk<{success: boolean}, {paymentId: string}, ThunkApi>(
    `${actionType}/deleteSaleHistoryDetailByApi`,
    async ({paymentId}) => {
        return createWrapper<SchemasOrderSaleHistory['/payment/:id']['response']>(api.delete(`/payment/${paymentId}`))
    },
)

export const getSaleHistoryDetailByApi = createApiAsyncThunk<SaleHistoryDetailResponse, {orderNo: string}, ThunkApi>(
    `${actionType}/getSaleHistoryDetail`,
    async ({orderNo}) => {
        return createWrapper<SchemasOrderSaleHistory['/:orderNo']['response']>(api.get(`/${orderNo}`))
    },
)

export const getSaleHistoryAssignmentsByApi = createApiAsyncThunk<
    SaleHistoryAssignmentsResponse,
    {productOrderNos: string[]},
    ThunkApi
>(`${actionType}/getSaleHistoryAssignmentsByApi`, async ({productOrderNos}) => {
    return createWrapper<SchemasOrderSaleHistory['/assignments']['response']>(
        // query 를 줄이기 위해, post 로 전달
        api.post(`/assignments`, {
            productOrderNos,
        }),
    )
})

export const getSaleHistoryAssignmentsDetailByApi = createApiAsyncThunk<
    SaleHistoryAssignmentsDetailResponse,
    {orderNo: string; delay?: boolean},
    ThunkApi
>(`${actionType}/getSaleHistoryAssignmentsDetailByApi`, async ({orderNo, delay}) => {
    if (delay) {
        await new Promise((resolve) => setTimeout(resolve, 3000))
    }
    return createWrapper<SchemasOrderSaleHistory['/assignments/:orderNo']['response']>(
        api.get(`/assignments/${orderNo}`),
    )
})

/** 검색창 검색 */
export const searchSaleHistory = createAsyncThunk<
    void,
    SearchParams & {
        page?: number
        type?: 'PUSH' | 'REPLACE'
    },
    ThunkApi
>(`${actionType}/search`, ({type, page, keyword, category, startDate, endDate, search}, {dispatch}) => {
    const url = getUrl({
        url: `${SaleHistoryUrl}`,
        page,
        search,
        keyword,
        category,
        startDate,
        endDate,
    })
    dispatch(
        type === 'REPLACE'
            ? replace(url, {
                  resetScroll: true,
              })
            : push(url, {
                  resetScroll: true,
              }),
    )
})

export const goSearchSaleHistory = createAsyncThunk<void, SearchParams, ThunkApi>(
    `${actionType}/go/search`,
    ({keyword, category, startDate, endDate}, {dispatch}) => {
        dispatch(
            push(
                getUrl({
                    url: `${SaleHistorySearchUrl}`,
                    keyword,
                    category,
                    startDate,
                    endDate,
                }),
            ),
        )
    },
)
